import React, {useState} from 'react';
import {Button, DatePicker, Form, Input, InputNumber, Select, Table} from "antd";
import {DeleteOutlined, InfoCircleOutlined, PlusOutlined} from "@ant-design/icons";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import {internalApi} from "../../../common/axios";
import {EventSegmentEntity, newSegmentItem} from "./EventSegmentEntity";

const tip = (title) => ({title, icon: <InfoCircleOutlined style={{color: '#1890ff'}}/>});
const half = {marginBottom: 8, width: '45%'};

// 이상 ~ 미만 범위 입력
const RangeInput = ({row, minKey, maxKey, onChange, step = 1, precision = 0}) => (
    <div style={{display: 'flex', gap: 4, alignItems: 'center'}}>
        <InputNumber value={row[minKey]} size="small" min={0} step={step} precision={precision} style={{flex: 1}}
                     onChange={v => onChange(minKey, v ?? 0)}/>
        <span style={{fontSize: 11, color: '#888', whiteSpace: 'nowrap'}}>이상 ~</span>
        <InputNumber value={row[maxKey]} size="small" min={0} step={step} precision={precision} style={{flex: 1}}
                     onChange={v => onChange(maxKey, v ?? 0)}/>
        <span style={{fontSize: 11, color: '#888', whiteSpace: 'nowrap'}}>미만</span>
    </div>
);

// 개인화 테이블: 세그먼트 그룹 공통 조건(event_segment) + 그룹 이벤트별 조건(event_segment_group) 편집
const EventSegmentEditModal = ({record, eventTable, handleClose}) => {
    const [row, setRow] = useState(new EventSegmentEntity(record));
    const [saving, setSaving] = useState(false);

    const handleSetRow = (key, value) => setRow(prev => new EventSegmentEntity({...prev, [key]: value}));
    const handleSetItem = (index, key, value) =>
        handleSetRow('items', row.items.map((item, i) => i === index ? {...item, [key]: value} : item));

    const eventOptions = Object.values(eventTable || {}).map(e => ({value: e.index, label: `${e.nameText || ''}(${e.index})`}));
    const itemColumns = [
        {
            title: '이벤트', key: 'typeIndex', width: 300,
            render: (_, item, index) => (
                <Select value={item.typeIndex || undefined} size="small" showSearch placeholder="이벤트 선택" optionFilterProp="label"
                        style={{width: '100%'}} options={eventOptions} onChange={v => handleSetItem(index, 'typeIndex', v)}/>
            ),
        },
        {
            title: '평균 결제 금액(달러)', key: 'avrPayment', width: 260,
            render: (_, item, index) => (
                <RangeInput row={item} minKey="minAvrPayment" maxKey="maxAvrPayment" step={0.01} precision={2}
                            onChange={(key, value) => handleSetItem(index, key, value)}/>
            ),
        },
        {
            title: '레벨', key: 'level', width: 220,
            render: (_, item, index) => (
                <RangeInput row={item} minKey="minLevel" maxKey="maxLevel" onChange={(key, value) => handleSetItem(index, key, value)}/>
            ),
        },
        {
            key: 'delete', width: 50,
            render: (_, __, index) => (
                <Button size="small" danger icon={<DeleteOutlined/>}
                        onClick={() => handleSetRow('items', row.items.filter((_, i) => i !== index))}/>
            ),
        },
    ];

    const handleSave = async () => {
        if (row.isInvalidate()) return;
        setSaving(true);
        try {
            await internalApi.post('events/segment/condition', {data: row.toJson()});
            toast.success('저장 완료');
            handleClose();
        } catch {
            toast.error('저장 실패');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            await internalApi.delete('events/segment/condition', {params: {seq: row.seq}});
            toast.success('삭제 완료');
            handleClose();
        } catch {
            // 사용 중인 조건이면 서버가 사유를 내려준다 (axios 인터셉터가 표시)
        }
    };

    return (
        <div>
            <Form layout="inline">
                <Form.Item label="메모" style={half}>
                    <Input value={row.memo} size="small" onChange={e => handleSetRow('memo', e.target.value)}/>
                </Form.Item>
                <Form.Item label="마지막 결제일" tooltip={tip('N일 동안 결제하지 않은 유저. 0이면 모두')} style={half}>
                    <InputNumber value={row.lastPayment} size="small" min={0} addonAfter="일" style={{width: '100%'}}
                                 onChange={v => handleSetRow('lastPayment', v ?? 0)}/>
                </Form.Item>
                <Form.Item label="평균 결제일" tooltip={tip('평균 결제 금액 = 최근 N일 총 금액 / 구매 횟수')} style={half}>
                    <InputNumber value={row.averagePayment} size="small" min={0} addonAfter="일" style={{width: '100%'}}
                                 onChange={v => handleSetRow('averagePayment', v ?? 0)}/>
                </Form.Item>
                <Form.Item label="결제 횟수(이상)" style={half}>
                    <InputNumber value={row.paymentCount} size="small" min={0} style={{width: '100%'}}
                                 onChange={v => handleSetRow('paymentCount', v ?? 0)}/>
                </Form.Item>
                <Form.Item label="누적 기준일" tooltip={tip('누적 로그인, 누적 결제 금액의 기준일')} style={half}>
                    <DatePicker value={dayjs(row.baseDate)} size="small" style={{width: '100%'}} allowClear={false}
                                onChange={(_, dateStr) => handleSetRow('baseDate', dateStr)}/>
                </Form.Item>
                <Form.Item label="누적 로그인 일수(이상)" tooltip={tip('최근 90일 이내만 집계')} style={half}>
                    <InputNumber value={row.cumulativeLoginCount} size="small" min={0} style={{width: '100%'}}
                                 onChange={v => handleSetRow('cumulativeLoginCount', v ?? 0)}/>
                </Form.Item>
                <Form.Item label="누적 결제 금액(달러)" tooltip={tip('이하 + 0 이면 무과금 유저, 이상 + 0 이면 조건 미사용')} style={half}>
                    <div style={{display: 'flex', gap: 4}}>
                        <InputNumber value={row.cumulativePayment} size="small" min={0} style={{flex: 1}}
                                     onChange={v => handleSetRow('cumulativePayment', v ?? 0)}/>
                        <Select value={row.isCumPaymentOver} size="small" style={{width: 80}}
                                onChange={v => handleSetRow('isCumPaymentOver', v)}
                                options={[{value: 1, label: '이상▲'}, {value: 0, label: '이하▼'}]}/>
                    </div>
                </Form.Item>
            </Form>

            <div style={{border: '2px solid #1e4d7b', borderRadius: 8, padding: 12, marginTop: 8}}>
                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8}}>
                    <span style={{fontWeight: 'bold'}}>이벤트 목록 (평균 결제 금액·레벨 미만값이 0이면 제한 없음)</span>
                    <Button size="small" icon={<PlusOutlined/>} onClick={() => handleSetRow('items', [...row.items, newSegmentItem()])}>이벤트 추가</Button>
                </div>
                <Table columns={itemColumns} dataSource={row.items.map((item, i) => ({...item, key: i}))}
                       pagination={false} size="small" scroll={{x: 'max-content'}}/>
            </div>

            <div style={{marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
                {row.seq > 0 && <Button danger onClick={handleDelete}>삭제</Button>}
                <Button onClick={handleClose}>취소</Button>
                <Button type="primary" loading={saving} onClick={handleSave}>저장</Button>
            </div>
        </div>
    );
};

export default EventSegmentEditModal;
